/* Rich-text (Markdown) editor for task descriptions. Built on TipTap; styled
   with --rv-* / foundation tokens to match the dashboard form controls. */

.Editor {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--rv-border);
  border-radius: var(--border-radius-md);
  background: var(--rv-bg-app);
}

.Editor:focus-within {
  border-color: var(--rv-accent);
  box-shadow: var(--rv-shadow-ring-focus);
}

.EditorFill {
  flex: 1;
  min-height: 0;
}

.EditorFill .Surface {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.EditorFill .Surface > :last-child {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.EditorFill .Content {
  flex: 1;
  min-height: var(--dropdown-height-large);
  max-height: none;
}

/* ── toolbar ── */

.Toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3xs);
  padding: var(--space-xs);
  border-bottom: 1px solid var(--rv-border);
  background: var(--rv-bg-app-subtle);
}

.ToolButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--space-2xl);
  height: var(--space-2xl);
  padding: 0;
  border: none;
  border-radius: var(--border-radius-sm);
  background: none;
  color: var(--rv-fg-3);
  cursor: pointer;
  transition:
    background var(--rv-dur-fast) var(--rv-ease-out),
    color var(--rv-dur-fast) var(--rv-ease-out);
}

.ToolButton:hover {
  background: var(--rv-surface);
  color: var(--rv-fg-1);
}

.ToolButton:disabled {
  cursor: default;
  opacity: 50%;
}

.ToolOn {
  background: var(--rv-accent-glow-12);
  color: var(--rv-accent-ink);
}

.ToolOn:hover {
  background: var(--rv-accent-glow-20);
  color: var(--rv-accent-ink);
}

.ToolButton svg {
  width: var(--icon-size-sm);
  height: var(--icon-size-sm);
}

.Divider {
  width: 0;
  height: var(--space-lg);
  margin: 0 var(--space-3xs);
  border-left: 1px solid var(--rv-border);
}

/* ── inline link editor ── */

.LinkBar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-xs);
  border-bottom: 1px solid var(--rv-border);
  background: var(--rv-bg-app-subtle);
}

.LinkInput {
  flex: 1 1 auto;
  min-width: var(--container-width-sm);
}

.LinkAction {
  flex: 0 0 auto;
}

.LinkActionIcon {
  width: var(--icon-size-xs);
  height: var(--icon-size-xs);
  margin-right: var(--space-3xs);
}

.LinkClose {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: var(--space-2xl);
  height: var(--space-2xl);
  padding: 0;
  border: none;
  border-radius: var(--border-radius-sm);
  background: none;
  color: var(--rv-fg-3);
  cursor: pointer;
}

.LinkClose:hover {
  background: var(--rv-surface);
  color: var(--rv-fg-1);
}

.LinkClose svg {
  width: var(--icon-size-sm);
  height: var(--icon-size-sm);
}

/* Owns the bottom rule so the error line, when present, sits inside the bar
   rather than below its border. */
.ImageBar {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--rv-border);
}

.ImageBar .LinkBar {
  border-bottom: none;
}

.BarError {
  margin: 0;
  padding: 0 var(--space-xs) var(--space-sm);
  font-size: var(--font-size-xs);
  color: var(--content-foundation-content-danger);
  background: var(--rv-bg-app-subtle);
}

/* ── editable surface ── */

.Surface {
  position: relative;
}

.Placeholder {
  position: absolute;
  top: var(--space-md);
  left: var(--space-lg);
  color: var(--rv-fg-4);
  font-size: var(--font-size-sm);
  pointer-events: none;
}

/* The global iOS focus-zoom guard (css/index.css) raises every editable
   surface to 16px on touch devices, the editable ProseMirror host included.
   The placeholder is a sibling overlay, not a descendant of that host, so it
   keeps its 14px and the hint sits on a different baseline from the caret it
   is standing in for. Match the surface. */
@media (pointer: coarse) {
  .Placeholder {
    font-size: var(--font-size-md);
  }
}

.Content {
  min-height: var(--space-9xl);
  max-height: var(--container-width-sm);
  padding: var(--space-md) var(--space-lg);
  overflow-y: auto;
  outline: none;
}

/* Shared by the editor surface and read-only conversation bodies so a
   composed message looks the same as a task description. */
.Prose {
  color: var(--rv-fg-1);
  font-family: var(--rv-font-ui);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.Prose > :first-child {
  margin-top: 0;
}

.Prose > :last-child {
  margin-bottom: 0;
}

.Prose p {
  margin: 0 0 var(--space-sm);
}

.Prose h1,
.Prose h2,
.Prose h3 {
  margin: var(--space-lg) 0 var(--space-sm);
  font-family: var(--rv-font-display);
  font-weight: var(--font-weight-semibold);
  line-height: 1.25;
}

.Prose h1 {
  font-size: var(--font-size-lg);
}

.Prose h2 {
  font-size: var(--font-size-md);
}

.Prose h3 {
  font-size: var(--font-size-sm);
}

.Prose ul,
.Prose ol {
  margin: 0 0 var(--space-sm);
  padding-left: var(--space-xl);

  /* Preflight and the design-system reset zero list-style on every ul/ol.
     Revert to the user-agent markers so bullets and numbers show in the
     composer and in the thread — they are not a colour, so this is the same
     in light and dark. */
  list-style: revert;
}

.Prose li {
  margin: var(--space-3xs) 0;
}

.Prose a {
  color: var(--rv-accent-ink);
  text-decoration: underline;
}

.Prose blockquote {
  margin: 0 0 var(--space-sm);
  padding-left: var(--space-md);
  border-left: 2px solid var(--rv-border);
  color: var(--rv-fg-3);
}

.Prose code {
  padding: var(--space-3xs) var(--space-xs);
  border-radius: var(--border-radius-xs);
  background: var(--rv-bg-app-subtle);
  font-family: var(--rv-font-mono);
  font-size: var(--font-size-xs);
}

.Prose pre {
  margin: 0 0 var(--space-sm);
  padding: var(--space-md);
  border-radius: var(--border-radius-md);
  background: var(--rv-bg-app-subtle);
  overflow-x: auto;
}

.Prose pre code {
  padding: 0;
  background: none;
}

.Prose hr {
  margin: var(--space-lg) 0;
  border: none;
  border-top: 1px solid var(--rv-border);
}

.Prose img {
  max-width: 100%;
  height: auto;
  border-radius: var(--border-radius-sm);
}

/* ProseMirror marks the selected node; without this an image gives no
   feedback that it is selected and about to be replaced or deleted.
   `:global` because ProseMirror adds the class at runtime — a CSS-module
   local name would be hashed and never match. Matched as an attribute rather
   than written as `.ProseMirror-selectednode`: the name is ProseMirror's, not
   ours, so it cannot be renamed to satisfy our class-naming convention. */
.Prose img:global([class~="ProseMirror-selectednode"]) {
  outline: 1px solid var(--rv-accent);
  outline-offset: var(--space-3xs);
}

.MissingToken {
  color: var(--rv-danger-ink);
  background-color: var(--rv-danger-bg);
  border-radius: var(--border-radius-xs);
}
